/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { useTranslation as useI18nTranslation } from 'react-i18next';
import useLanguageSelection from '@src/hooks/useLanguageSelection';
import { isLanguageLocked } from '@src/i18n/languagePolicy';
import useTranslation from '@src/hooks/useTranslation';
import { GlobeIcon } from '@src/components/common/icons/GlobeIcon';
import { BlackChevronLeftIcon } from '@src/components/common/icons/BlackChevronLeftIcon';
import dakastarLogo from '@src/assets/images/dakastarLogo.svg';
import teachesLogo from '@src/assets/images/teachesLogo.svg';

const PageWrapper = styled.div`
	position: relative;
	width: 100%;
	min-width: 340px;
	min-height: calc(var(--vh, 1vh) * 100);
	display: flex;
	flex-direction: column;
	align-items: center;
	background: #F5F5F7;
`;

const GradientBg = styled.div`
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	height: 173px;
	filter: blur(50px);
	display: flex;
	pointer-events: none;

	body.hide-hr-registration-glow & {
		display: none;
	}
`;

const Header = styled.div`
	width: 100%;
	height: 72px;
	padding: 20px 40px;
	display: flex;
	align-items: center;
	justify-content: space-between;

	@media ${props => props.theme.device.mobile} {
		padding: 20px;
	}
`;

const LogoWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 16px;
`;

const LogoImg = styled.img`
	width: 110px;

	@media (max-width: 768px) {
		width: 82px;
	}
`;

const LogoDivider = styled.div`
	width: 1px;
	height: 18px;
	background: #333;
`;

const BackButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	padding: 0;
	background: transparent;
	border: 0;
	cursor: pointer;
`;

const LanguageButton = styled.button`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 4px 12px;
	border-radius: 4px;
	border: 1px solid #676767;
	background: transparent;
	font-size: 14px;
  font-weight: 400;
  line-height: 22px;
	color: #676767;
	cursor: pointer;

	&:hover:not(:disabled) {
		border-color: #222;
		color: #222;
	}
	&:disabled {
		cursor: default;
	}
`;

const ContentArea = styled.div`
	position: relative;
	width: 100%;
	margin-bottom: 20px;
	z-index: 2;
`;

interface HrBusinessWrapperProps {
	children: React.ReactNode;
}

const HrBusinessWrapper: React.FC<HrBusinessWrapperProps> = ({ children }) => {
	const { pathname } = useRouter();
	const { t } = useTranslation();
	const { i18n } = useI18nTranslation();
	const { selectLanguage } = useLanguageSelection();
	const [showOnboardingBackButton, setShowOnboardingBackButton] = useState(false);
	const isEnglish = i18n.language?.toLowerCase().split('-')[0] === 'en';

	const handleLanguageToggle = () => {
		selectLanguage(isEnglish ? 'zh-CHT' : 'en');
	};

	useEffect(() => {
		setShowOnboardingBackButton(document.body.classList.contains('hide-hr-registration-glow'));

		const handleOnboardingMode = (event: Event) => {
			const customEvent = event as CustomEvent<{ enabled?: boolean; showBackButton?: boolean }>;
			setShowOnboardingBackButton(Boolean(customEvent.detail?.enabled) && Boolean(customEvent.detail?.showBackButton));
		};

		window.addEventListener('hr-registration-onboarding-mode', handleOnboardingMode);

		return () => {
			window.removeEventListener('hr-registration-onboarding-mode', handleOnboardingMode);
		};
	}, []);

	const handleClickBack = () => {
		window.dispatchEvent(new Event('hr-registration-onboarding-back'));
	};

	return (
		<PageWrapper>
			<Header>
				{showOnboardingBackButton ? (
					<BackButton type="button" onClick={handleClickBack} aria-label={t('common:action:back')}>
						<BlackChevronLeftIcon width={24} height={24} />
					</BackButton>
				) : (
					<LogoWrapper>
						<LogoImg src={dakastarLogo} alt={t('hr:onboarding:register:dakastar_logo_alt')} />
						<LogoDivider />
						<LogoImg src={teachesLogo} alt="Teaches" />
					</LogoWrapper>
				)}
				<LanguageButton type="button" disabled={isLanguageLocked(pathname)} onClick={handleLanguageToggle}>
					<GlobeIcon />
					<span>{t(isEnglish ? 'common:language:name:en' : 'common:language:zh_tw_short')}</span>
				</LanguageButton>
			</Header>
			<ContentArea>
				{children}
			</ContentArea>
			<GradientBg />
		</PageWrapper>
	);
};

export default HrBusinessWrapper;
